'use client';

/**
 * MobileCopilotSheet — slide-up bottom sheet hosting the right rail.
 *
 * Renders the same three surfaces as the desktop RailColumn — Insights,
 * Chat, Composer — driven by `useRailSurfaceStore`. Legacy Copilot is
 * excluded (decommissioning). A mini tab strip at the top lets the user
 * switch surfaces without closing the sheet.
 *
 * Sizing: single detent at ~88% viewport height so Canvas peeks at the
 * top, giving the user a tap-target to dismiss.
 *
 * @component_role: Mobile Rail Sheet
 * @component_type: Layout Overlay
 * @domain: Frontend Shell
 * @layer: presentation
 */

import { useEffect } from 'react';
import { X, LayoutList, MessageSquare, ShoppingCart } from 'lucide-react';

import { InsightsPanel } from '@/components/copilot/rail-insights/insights-panel';
import { ChatSurface } from '@/components/copilot/chat/chat-surface';
import { ComposerSurface } from '@/components/copilot/composer/composer-surface';
import { cn } from '@/lib/utils/cn';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useRailSurfaceStore, type RailSurface } from '@/lib/stores/rail-surface-store';

const SURFACES: { key: RailSurface; label: string; Icon: typeof LayoutList }[] = [
  { key: 'insights', label: 'Insights', Icon: LayoutList },
  { key: 'chat', label: 'Chat', Icon: MessageSquare },
  { key: 'composer', label: 'Cart', Icon: ShoppingCart },
];

export function MobileCopilotSheet() {
  const activeOverlay = useLayoutStore((s) => s.mobileActiveOverlay);
  const setActiveOverlay = useLayoutStore((s) => s.setMobileActiveOverlay);
  const isOpen = activeOverlay === 'copilot';
  const close = () => setActiveOverlay(null);

  const active = useRailSurfaceStore((s) => s.active);
  const setActive = useRailSurfaceStore((s) => s.setActive);
  const effectiveActive = active === 'copilot' ? 'insights' : active;

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div
      aria-hidden={!isOpen}
      className={cn(
        'fixed inset-0 z-50',
        isOpen ? 'pointer-events-auto' : 'pointer-events-none',
      )}
    >
      {/* Backdrop */}
      <div
        onClick={close}
        className={cn(
          'absolute inset-0 bg-black/50 transition-opacity duration-200',
          isOpen ? 'opacity-100' : 'opacity-0',
        )}
      />

      {/* Sliding panel */}
      <aside
        role="dialog"
        aria-label="Rail"
        className={cn(
          'absolute left-0 right-0 bottom-0 h-[88%] bg-background border-t border-border',
          'flex flex-col rounded-t-xl shadow-xl transition-transform duration-200 ease-out',
          isOpen ? 'translate-y-0' : 'translate-y-full',
        )}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Grab handle */}
        <div className="flex items-center justify-center pt-2 pb-1">
          <div className="h-1 w-10 rounded-full bg-muted" aria-hidden="true" />
        </div>

        {/* Surface tabs + close */}
        <div className="h-10 flex items-center px-2 border-b border-border gap-1">
          {SURFACES.map((s) => {
            const isActive = effectiveActive === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setActive(s.key)}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
                  isActive
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                )}
              >
                <s.Icon className="h-3.5 w-3.5" />
                {s.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="ml-auto p-1 -mr-1 text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Surface body */}
        <div className="flex-1 min-h-0 overflow-hidden relative">
          <div className={cn('absolute inset-0', effectiveActive !== 'insights' && 'hidden')}>
            <InsightsPanel />
          </div>
          <div className={cn('absolute inset-0', effectiveActive !== 'chat' && 'hidden')}>
            <ChatSurface />
          </div>
          <div className={cn('absolute inset-0', effectiveActive !== 'composer' && 'hidden')}>
            <ComposerSurface />
          </div>
        </div>
      </aside>
    </div>
  );
}
